/**
 * Device detail — read view + operator-facing transition buttons.
 *
 * Mirrors the legal-transition table in `@dfp/domain`. System-driven
 * transitions (ActivateLoan, StartReleaseCoolingPeriod, MarkPaidOff,
 * ReleaseOwnership, BeginRepossession, CompleteRepossession,
 * BeginRefurbishment, FinishRefurbishment, Resell, CancelRepossession)
 * are intentionally omitted — they run from apps/workers, not the UI.
 * RecoveredFromLossOrTheft IS exposed (the "Recover" button) so an operator
 * can reinstate a device that was marked lost/stolen.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import {
  ApiError,
  type Contract,
  type Customer,
  type Dealer,
  type Device,
  type DeviceCommand,
  type DeviceLifecycleState,
  type DeviceLocationEvent,
  type DeviceTransitionRequest,
  type Loan,
  type LockEvent,
  type OnboardingStatus,
} from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { Select } from "../components/Form.js";
import { DeviceLocationMap } from "../components/DeviceLocationMap.js";
import { Collapsible } from "../components/Collapsible.js";
import { DeviceRestrictionsCard } from "../components/DeviceRestrictionsCard.js";
import { EnrollmentQrPanel } from "../components/EnrollmentQrPanel.js";
import { ForceSyncButton } from "../components/ForceSyncButton.js";
import { OnboardingStatusCard } from "../components/OnboardingStatusCard.js";
import { RequestApprovalForm } from "../components/RequestApprovalForm.js";
import { ServicePinCard } from "../components/ServicePinCard.js";
import { DeviceContactsCard } from "../components/DeviceContactsCard.js";
import { DeviceMonitoringCard } from "../components/DeviceMonitoringCard.js";
import { DeviceMonitoringReveal } from "../components/DeviceMonitoringReveal.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { cn, formatDateTime, formatMoney } from "../lib/utils.js";

interface TransitionDef {
  readonly label: string;
  readonly kind: DeviceTransitionRequest["kind"];
  readonly needsReason?: boolean;
  /** AssignToDealer needs a dealerId payload. */
  readonly needsDealerId?: boolean;
  /** MarkLost/MarkStolen accept an optional customer-facing lost-mode message. */
  readonly needsMessage?: boolean;
  readonly variant?: "primary" | "secondary" | "danger";
  readonly allowedFrom: readonly DeviceLifecycleState[];
  readonly permission: string;
}

const TRANSITIONS: readonly TransitionDef[] = [
  {
    label: "Receive at warehouse",
    kind: "ReceiveAtWarehouse",
    allowedFrom: ["PROCURED"],
    permission: "device.write",
    variant: "primary",
  },
  {
    label: "Register device",
    kind: "RegisterDevice",
    allowedFrom: ["WAREHOUSE_RECEIVED"],
    permission: "device.write",
    variant: "primary",
  },
  {
    label: "Enroll control",
    kind: "EnrollControl",
    allowedFrom: ["DEVICE_REGISTERED"],
    permission: "device.write",
    variant: "primary",
  },
  {
    label: "Assign to dealer",
    kind: "AssignToDealer",
    needsDealerId: true,
    allowedFrom: ["CONTROL_ENROLLED"],
    permission: "dealer.assign_stock",
    variant: "primary",
  },
  {
    label: "Reserve for customer",
    kind: "ReserveForCustomer",
    allowedFrom: ["ASSIGNED_TO_DEALER"],
    permission: "device.write",
    variant: "secondary",
  },
  {
    label: "Unreserve",
    kind: "UnreserveForCustomer",
    allowedFrom: ["RESERVED_FOR_CUSTOMER"],
    permission: "device.write",
    variant: "secondary",
  },
  {
    label: "Hand over",
    kind: "HandOver",
    allowedFrom: ["RESERVED_FOR_CUSTOMER"],
    permission: "device.write",
    variant: "primary",
  },
  {
    label: "Return from dealer",
    kind: "ReturnFromDealer",
    allowedFrom: ["ASSIGNED_TO_DEALER"],
    permission: "device.write",
    variant: "secondary",
  },
  {
    label: "Mark damaged",
    kind: "MarkDamaged",
    needsReason: true,
    allowedFrom: ALL_NON_TERMINAL(),
    permission: "device.write",
    variant: "secondary",
  },
  {
    label: "Mark lost",
    kind: "MarkLost",
    needsReason: true,
    needsMessage: true,
    allowedFrom: ALL_NON_TERMINAL(),
    permission: "device.write",
    variant: "danger",
  },
  {
    label: "Mark stolen",
    kind: "MarkStolen",
    needsReason: true,
    needsMessage: true,
    allowedFrom: ALL_NON_TERMINAL(),
    permission: "device.write",
    variant: "danger",
  },
  {
    label: "Write off",
    kind: "WriteOff",
    needsReason: true,
    allowedFrom: ALL_NON_TERMINAL(),
    permission: "device.write_off",
    variant: "danger",
  },
  {
    // Reinstate a recovered device. LOST → ACTIVE_FINANCED (back in service),
    // STOLEN → REPOSSESSING (recovered into the repossession workflow).
    label: "Recover (reinstate)",
    kind: "RecoveredFromLossOrTheft",
    allowedFrom: ["LOST", "STOLEN"],
    permission: "device.write",
    variant: "primary",
  },
];

function ALL_NON_TERMINAL(): readonly DeviceLifecycleState[] {
  return [
    "PROCURED",
    "WAREHOUSE_RECEIVED",
    "DEVICE_REGISTERED",
    "CONTROL_ENROLLED",
    "ASSIGNED_TO_DEALER",
    "RESERVED_FOR_CUSTOMER",
    "HANDED_OVER",
    "ACTIVE_FINANCED",
    "PENDING_RELEASE",
    "PAID_OFF",
    "REPOSSESSING",
    "REPOSSESSED",
    "REFURBISHING",
    "READY_FOR_RESALE",
    "DAMAGED",
    "LOST",
    "STOLEN",
  ];
}

export function DeviceDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id: string };
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [activeKind, setActiveKind] = useState<DeviceTransitionRequest["kind"] | null>(null);
  const [reasonDraft, setReasonDraft] = useState("");
  const [dealerIdDraft, setDealerIdDraft] = useState("");
  const [messageDraft, setMessageDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const deviceQuery = useQuery({
    queryKey: ["devices", "byId", id],
    queryFn: () => api.get<Device>(`/api/devices/${id}`),
  });

  // For the "Assign to dealer" action — a picklist beats typing a UUID.
  const dealersQuery = useQuery({
    queryKey: ["dealers", "list"],
    queryFn: () => api.get<readonly Dealer[]>("/api/dealers"),
  });

  // Three companion queries — kept independent of the main device fetch
  // so a failure in any one panel doesn't blank the whole page. Each
  // panel renders a "—" state when empty. Permission-gated at the API
  // (device.read); the auth guard already controls page access so we
  // don't dupe-gate here.
  const lockEventsQuery = useQuery({
    queryKey: ["devices", "lockEvents", id],
    queryFn: () => api.get<readonly LockEvent[]>(`/api/devices/${id}/lock-events`),
  });
  const commandsQuery = useQuery({
    queryKey: ["devices", "commands", id],
    queryFn: () => api.get<readonly DeviceCommand[]>(`/api/devices/${id}/commands`),
  });
  const locationsQuery = useQuery({
    queryKey: ["devices", "locations", id],
    queryFn: () => api.get<readonly DeviceLocationEvent[]>(`/api/devices/${id}/locations`),
  });
  // P2.C onboarding gap #4 — refreshes every 15 s so a freshly-
  // enrolled device flips from RED to AMBER to GREEN without the
  // dealer having to reload the page.
  const onboardingQuery = useQuery({
    queryKey: ["devices", "onboarding", id],
    queryFn: () => api.get<OnboardingStatus>(`/api/devices/${id}/onboarding-status`),
    refetchInterval: 15_000,
  });

  // Active loan + downstream customer/contract for the cross-link panel.
  // No /loans?deviceId= endpoint today; pull the country's loans list
  // and pick the freshest non-terminal one bound to this device. Volumes
  // are small + sorted server-side; acceptable until a scoped endpoint
  // lands.
  const activeLoanQuery = useQuery({
    queryKey: ["loans", "byDevice", id],
    queryFn: async () => {
      const all = await api.get<readonly Loan[]>("/api/loans");
      const candidates = all.filter(
        (l) => l.deviceId === id && l.status !== "PAID_OFF" && l.status !== "WRITTEN_OFF",
      );
      // Newest first — matches the server's createdAt DESC.
      candidates.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
      return candidates[0] ?? null;
    },
  });
  const activeLoan = activeLoanQuery.data;
  const customerQuery = useQuery({
    queryKey: ["customers", "byId", activeLoan?.customerId],
    queryFn: () => api.get<Customer>(`/api/customers/${activeLoan?.customerId}`),
    enabled: Boolean(activeLoan?.customerId),
  });
  const contractQuery = useQuery({
    queryKey: ["contracts", "byId", activeLoan?.contractId],
    queryFn: () => api.get<Contract>(`/api/contracts/${activeLoan?.contractId}`),
    enabled: Boolean(activeLoan?.contractId && activeLoan.contractId.length > 0),
  });

  const transition = useMutation({
    mutationFn: (body: DeviceTransitionRequest) =>
      api.post<Device>(`/api/devices/${id}/transitions`, body),
    onSuccess: (next) => {
      queryClient.setQueryData<Device>(["devices", "byId", id], next);
      void queryClient.invalidateQueries({ queryKey: ["devices", "list"] });
      setActiveKind(null);
      setReasonDraft("");
      setDealerIdDraft("");
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Transition rejected");
      } else {
        setError("Network error");
      }
    },
  });

  // Force-release: complete a PENDING_RELEASE loan now, skipping the cooling
  // window. The endpoint emits the cooling-period-due event; the worker
  // completes the release asynchronously, so we invalidate the surfaces that
  // will flip and show a "triggered" confirmation.
  const [forceReleaseDone, setForceReleaseDone] = useState(false);
  const [forceReleaseError, setForceReleaseError] = useState<string | null>(null);
  const forceRelease = useMutation({
    mutationFn: (loanId: string) =>
      api.post<{ loanId: string; releaseTriggered: boolean }>(
        `/api/control/loans/${loanId}/force-release`,
        {},
      ),
    onSuccess: () => {
      setForceReleaseError(null);
      setForceReleaseDone(true);
      void queryClient.invalidateQueries({ queryKey: ["devices", "byId", id] });
      void queryClient.invalidateQueries({ queryKey: ["loans", "byDevice", id] });
      void queryClient.invalidateQueries({ queryKey: ["devices", "commands", id] });
    },
    onError: (err: unknown) => {
      setForceReleaseError(
        err instanceof ApiError
          ? (err.problem.message ?? err.problem.detail ?? "Force-release rejected")
          : "Network error",
      );
    },
  });

  if (deviceQuery.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (deviceQuery.isError || !deviceQuery.data) {
    return <p className="text-sm text-red-700">Failed to load device.</p>;
  }
  const device = deviceQuery.data;
  const permitted = (perm: string): boolean => (auth.user?.permissions ?? []).includes(perm);
  const legal = TRANSITIONS.filter(
    (t) => t.allowedFrom.includes(device.lifecycleState) && permitted(t.permission),
  );

  function run(t: TransitionDef): void {
    setError(null);
    const needsForm = t.needsReason || t.needsDealerId || t.needsMessage;
    if (needsForm) {
      if (activeKind !== t.kind) {
        setActiveKind(t.kind);
        setReasonDraft("");
        setDealerIdDraft("");
        setMessageDraft("");
        return;
      }
      if (t.needsReason && reasonDraft.trim().length < 10) {
        setError("Reason must be at least 10 characters.");
        return;
      }
      if (
        t.needsDealerId &&
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          dealerIdDraft.trim(),
        )
      ) {
        setError("Dealer ID must be a valid UUID.");
        return;
      }
      if (t.needsMessage && messageDraft.trim().length > 280) {
        setError("Message must be 280 characters or fewer.");
        return;
      }
      const body: Record<string, unknown> = { kind: t.kind };
      if (t.needsReason) body["reason"] = reasonDraft.trim();
      if (t.needsDealerId) body["dealerId"] = dealerIdDraft.trim();
      // Optional — omit when blank so the device shows its default lost copy.
      if (t.needsMessage && messageDraft.trim()) body["message"] = messageDraft.trim();
      transition.mutate(body as DeviceTransitionRequest);
    } else {
      transition.mutate({ kind: t.kind } as DeviceTransitionRequest);
    }
  }

  const activeDef = legal.find((t) => t.kind === activeKind);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          {device.brand} {device.model}
        </h1>
        <p className="text-xs font-mono text-slate-500">{device.id}</p>
        {device.controlProvider === "android-do" ? (
          <p className="mt-1 text-xs text-slate-500">
            DPC version:{" "}
            {device.dpcVersionName ? (
              <span className="font-mono text-slate-700">
                {device.dpcVersionName} (code {device.dpcVersionCode ?? "?"})
              </span>
            ) : (
              <span className="text-slate-400">not reported yet</span>
            )}
          </p>
        ) : null}
        {device.controlProvider === "android-do" &&
        (device.dpcDeviceOwner != null || device.dpcUpdateStatus) ? (
          <p className="mt-0.5 text-xs text-slate-500">
            OTA self-update:{" "}
            {device.dpcDeviceOwner === true ? (
              <span className="text-slate-700">Device Owner ✓</span>
            ) : device.dpcDeviceOwner === false ? (
              <span className="font-medium text-amber-700">
                not Device Owner — can&apos;t self-update
              </span>
            ) : (
              <span className="text-slate-400">Device Owner unknown</span>
            )}
            {device.dpcUpdateStatus ? (
              <>
                {" · last install: "}
                <span
                  className={cn(
                    "font-mono",
                    device.dpcUpdateStatus === "SUCCESS" ? "text-green-700" : "text-amber-700",
                  )}
                >
                  {device.dpcUpdateStatus}
                </span>
                {device.dpcUpdateTargetVc ? ` (→ vc${device.dpcUpdateTargetVc})` : ""}
                {device.dpcUpdateStatusAt ? ` · ${formatDateTime(device.dpcUpdateStatusAt)}` : ""}
              </>
            ) : null}
          </p>
        ) : null}
      </div>

      <OnboardingStatusCard
        loading={onboardingQuery.isLoading}
        error={onboardingQuery.isError}
        status={onboardingQuery.data}
        enrollmentVerifiedAt={device.enrollmentVerifiedAt}
        enrolledManufacturer={device.enrolledManufacturer}
        enrolledModel={device.enrolledModel}
      />

      {device.controlProvider === "android-do" ? (
        <Collapsible
          bare
          title="Device restrictions"
          defaultOpen={device.enrollmentVerifiedAt == null}
        >
          <DeviceRestrictionsCard device={device} onboarding={onboardingQuery.data} />
        </Collapsible>
      ) : null}

      {/*
        Force sync — operator-triggered FCM nudge that makes the DPC
        post a snapshot + pull commands within ~5s. Gated on
        `device.sync` and only meaningful for android-do (Knox has its
        own backchannel). Rendered above EnrollmentQrPanel so the
        verification surfaces (onboarding card, status cells, lock
        events) sit between the trigger and the new data.
      */}
      {device.controlProvider === "android-do" && auth.user?.permissions.includes("device.sync") ? (
        <ForceSyncButton deviceId={device.id} />
      ) : null}

      {/*
        Force-release — only when the device's active loan is in the post-payoff
        cooling window (PENDING_RELEASE) and the operator can early-release.
        Completes the release now instead of waiting out release.cooling_hours.
      */}
      {activeLoan?.status === "PENDING_RELEASE" && permitted("loan.early_release") ? (
        <Card>
          <CardHeader>
            <CardTitle>Release</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <p className="text-slate-600">
              This device is in the post-payoff cooling period. It auto-releases when the window
              elapses (tunable via <code>release.cooling_hours</code> in Settings). Force-release
              completes it now — the worker flips the loan to PAID_OFF, releases the device, and
              clears Device Owner.
            </p>
            {forceReleaseDone ? (
              <p className="rounded-md bg-green-50 px-3 py-2 text-green-800">
                Release triggered — the device will be released within a few seconds. Reload to
                track progress.
              </p>
            ) : (
              <Button
                variant="danger"
                disabled={forceRelease.isPending}
                onClick={() => {
                  if (activeLoan) forceRelease.mutate(activeLoan.id);
                }}
              >
                {forceRelease.isPending ? "Releasing…" : "Force release now"}
              </Button>
            )}
            {forceReleaseError ? <p className="text-red-700">{forceReleaseError}</p> : null}
          </CardBody>
        </Card>
      ) : null}

      {device.controlProvider === "android-do" ? (
        <Collapsible
          bare
          title="DPC enrollment QR (minting)"
          defaultOpen={device.enrollmentVerifiedAt == null}
        >
          <EnrollmentQrPanel
            imei={device.imei}
            enrollmentVerifiedAt={device.enrollmentVerifiedAt}
          />
        </Collapsible>
      ) : null}

      {/*
        Service PIN card — only rendered for android-do devices (the
        cheat targets the DPC, which only exists in that provider) AND
        only for users with `device.service_pin.view`. Support /
        recovery roles get this by default; bare device.read scopes
        (loan_officer, compliance_officer, etc.) do not.
      */}
      {device.controlProvider === "android-do" &&
      auth.user?.permissions.includes("device.service_pin.view") ? (
        <ServicePinCard deviceId={device.id} />
      ) : null}

      {/*
        Synced contact book — decrypt + view (recovery). Gated on
        `device.contacts.view` (platform_admin + recovery_officer); third-party
        PII, every reveal audited. Only android-do devices sync contacts.
      */}
      {device.controlProvider === "android-do" &&
      auth.user?.permissions.includes("device.contacts.view") ? (
        <DeviceContactsCard deviceId={device.id} />
      ) : null}

      {auth.user?.permissions.includes("device.monitoring.manage") ? (
        <DeviceMonitoringCard device={device} />
      ) : null}

      {auth.user?.permissions.includes("device.monitoring.view") ? (
        <DeviceMonitoringReveal deviceId={device.id} />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Related</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
          <CrossLink
            label="Active loan"
            to="/loans/$id"
            id={activeLoan?.id}
            subtitle={
              activeLoanQuery.isLoading
                ? "Loading…"
                : activeLoan
                  ? `Status: ${activeLoan.status}`
                  : "No active loan"
            }
          />
          <CrossLink
            label="Customer"
            to="/customers/$id"
            id={customerQuery.data?.id}
            subtitle={
              customerQuery.data
                ? `${customerQuery.data.givenName} ${customerQuery.data.familyName}`
                : activeLoan
                  ? "Loading…"
                  : "—"
            }
          />
          <CrossLink
            label="Contract"
            to="/contracts/$id"
            id={contractQuery.data?.id}
            subtitle={
              contractQuery.data
                ? `${contractQuery.data.templateId} v${contractQuery.data.version}`
                : activeLoan?.contractId
                  ? "Loading…"
                  : "—"
            }
          />
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>State</CardTitle>
            <Badge tone={device.lifecycleState === "ACTIVE_FINANCED" ? "success" : "neutral"}>
              {device.lifecycleState}
            </Badge>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <Row k="IMEI" v={device.imei} />
            <Row k="Serial" v={device.serial} />
            <Row k="Value" v={formatMoney(device.valueAmountMinor, device.valueCurrency)} />
            <Row k="Risk class" v={device.modelRiskClass} />
            <Row
              k="Source"
              v={
                device.source === "dealer_provisioned"
                  ? "Dealer-provisioned"
                  : "Warehouse (central stock)"
              }
            />
            <Row k="Control provider" v={device.controlProvider} />
            <Row k="Assigned dealer" v={device.assignedDealerId ?? "—"} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Capabilities</CardTitle>
            <span className="text-xs text-slate-500">{device.capabilities.length}</span>
          </CardHeader>
          <CardBody>
            {device.capabilities.length === 0 ? (
              <p className="text-sm text-slate-500">None recorded.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {device.capabilities.map((c) => (
                  <li key={c}>
                    <Badge tone="neutral">{c}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Financing</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <Row
              k="Down payment"
              v={
                device.downPaymentMinor === null
                  ? "—"
                  : formatMoney(device.downPaymentMinor, device.valueCurrency)
              }
            />
            <Row k="Lending price" v={lendingPriceDisplay(device)} />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent locations</CardTitle>
          <span className="text-xs text-slate-500">{locationsQuery.data?.length ?? 0}</span>
        </CardHeader>
        <CardBody className="space-y-3">
          {locationsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : locationsQuery.isError ? (
            <p className="text-sm text-red-700">Failed to load locations.</p>
          ) : (
            <>
              <DeviceLocationMap rows={locationsQuery.data ?? []} />
              <LocationsList loading={false} error={false} rows={locationsQuery.data ?? []} />
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Command queue</CardTitle>
          <span className="text-xs text-slate-500">{commandsQuery.data?.length ?? 0}</span>
        </CardHeader>
        <CardBody>
          <CommandsList
            loading={commandsQuery.isLoading}
            error={commandsQuery.isError}
            rows={commandsQuery.data ?? []}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lock events</CardTitle>
          <span className="text-xs text-slate-500">{lockEventsQuery.data?.length ?? 0}</span>
        </CardHeader>
        <CardBody>
          <LockEventsList
            loading={lockEventsQuery.isLoading}
            error={lockEventsQuery.isError}
            rows={lockEventsQuery.data ?? []}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transitions</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {(() => {
            const routine = legal.filter((t) => t.variant !== "danger");
            const destructive = legal.filter((t) => t.variant === "danger");
            const canRequestApproval =
              device.lifecycleState !== "RELEASED" &&
              device.lifecycleState !== "RESOLD" &&
              device.lifecycleState !== "WRITTEN_OFF" &&
              device.lifecycleState !== "RETURNED" &&
              permitted("iam.approval.create");
            if (routine.length === 0 && destructive.length === 0 && !canRequestApproval) {
              return (
                <p className="text-sm text-slate-500">
                  No transitions available from state {device.lifecycleState}.
                </p>
              );
            }
            const hasDangerGroup = destructive.length > 0 || canRequestApproval;
            return (
              <div className="flex flex-wrap items-center gap-2">
                {routine.map((t) => (
                  <Button
                    key={t.kind}
                    variant={t.variant ?? "secondary"}
                    onClick={() => run(t)}
                    disabled={transition.isPending}
                  >
                    {t.label}
                  </Button>
                ))}
                {routine.length > 0 && hasDangerGroup ? (
                  <span aria-hidden className="mx-1 h-6 w-px self-center bg-slate-200" />
                ) : null}
                {destructive.map((t) => (
                  <Button
                    key={t.kind}
                    variant="danger"
                    onClick={() => run(t)}
                    disabled={transition.isPending}
                  >
                    {t.label}
                  </Button>
                ))}
                {canRequestApproval ? (
                  <RequestApprovalForm
                    permissionKey="device.write_off"
                    label="write-off device"
                    variant="danger"
                    payloadBuilder={(reason) => ({ deviceId: device.id, reason })}
                  />
                ) : null}
              </div>
            );
          })()}

          {activeDef ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
              {activeDef.needsDealerId ? (
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">Dealer</span>
                  <Select
                    value={dealerIdDraft}
                    onChange={(e) => setDealerIdDraft(e.target.value)}
                    className="mt-1"
                  >
                    <option value="">
                      {dealersQuery.isLoading ? "Loading dealers…" : "Select a dealer…"}
                    </option>
                    {(dealersQuery.data ?? []).map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                        {d.suspendedAt ? " (suspended)" : ""}
                      </option>
                    ))}
                  </Select>
                </label>
              ) : null}
              {activeDef.needsReason ? (
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">
                    Reason for {activeDef.kind} (≥ 10 chars)
                  </span>
                  <textarea
                    value={reasonDraft}
                    onChange={(e) => setReasonDraft(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </label>
              ) : null}
              {activeDef.needsMessage ? (
                <label className="block">
                  <span className="text-xs font-medium text-slate-700">
                    Message shown on the device (optional, ≤ 280 chars)
                  </span>
                  <textarea
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value)}
                    rows={2}
                    maxLength={280}
                    placeholder="e.g. This phone is reported lost. Please call +255…"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                  <span className="mt-1 block text-[11px] text-slate-400">
                    Left blank, the device shows the built-in lost-mode notice.
                  </span>
                </label>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setActiveKind(null);
                    setReasonDraft("");
                    setDealerIdDraft("");
                    setMessageDraft("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant={activeDef.variant ?? "secondary"}
                  onClick={() => run(activeDef)}
                  disabled={transition.isPending}
                >
                  Confirm {activeDef.kind}
                </Button>
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

function lendingPriceDisplay(device: Device): string {
  if (device.lendingPriceMode === null || device.lendingPriceMinor === null) return "Not priced";
  const amount = formatMoney(device.lendingPriceMinor, device.valueCurrency);
  return device.lendingPriceMode === "per_period" ? `${amount} / period` : `${amount} (fixed)`;
}

function Row({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs uppercase tracking-wide text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900 truncate">{v}</span>
    </div>
  );
}

interface PanelProps<T> {
  readonly loading: boolean;
  readonly error: boolean;
  readonly rows: readonly T[];
}

function PanelEmpty({
  loading,
  error,
  label,
}: {
  loading: boolean;
  error: boolean;
  label: string;
}): JSX.Element {
  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error) return <p className="text-sm text-red-700">Failed to load {label}.</p>;
  return <p className="text-sm text-slate-500">No {label} yet.</p>;
}

function LocationsList({ loading, error, rows }: PanelProps<DeviceLocationEvent>): JSX.Element {
  // Latest 5 by default (rows arrive newest-first); reveal the rest on
  // demand so a device with weeks of fixes doesn't flood the page.
  const [expanded, setExpanded] = useState(false);
  if (loading || error || rows.length === 0) {
    return <PanelEmpty loading={loading} error={error} label="locations" />;
  }
  const visible = expanded ? rows : rows.slice(0, 5);
  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {visible.map((row) => (
          <li
            key={row.id}
            className="flex flex-col gap-0.5 rounded-md border border-slate-200 px-3 py-2 text-xs"
          >
            <div className="flex justify-between gap-3">
              <a
                href={`https://www.google.com/maps?q=${row.lat},${row.lon}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-brand-600 hover:underline"
                title="Open in Google Maps"
              >
                {row.lat.toFixed(5)}, {row.lon.toFixed(5)}
              </a>
              <span className="text-slate-500">{new Date(row.observedAt).toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-3 text-slate-500">
              <span>{row.source}</span>
              <span>{row.accuracyM === null ? "no acc." : `± ${row.accuracyM.toFixed(0)} m`}</span>
            </div>
          </li>
        ))}
      </ul>
      {!expanded && rows.length > 5 ? (
        <button
          type="button"
          className="text-xs text-brand-600 hover:underline"
          onClick={() => setExpanded(true)}
        >
          Show {rows.length - 5} more
        </button>
      ) : null}
    </div>
  );
}

function CommandsList({ loading, error, rows }: PanelProps<DeviceCommand>): JSX.Element {
  if (loading || error || rows.length === 0) {
    return <PanelEmpty loading={loading} error={error} label="commands" />;
  }
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li
          key={row.id}
          className="flex flex-col gap-0.5 rounded-md border border-slate-200 px-3 py-2 text-xs"
        >
          <div className="flex justify-between gap-3">
            <span className="font-medium text-slate-900">
              {row.kind === "clear_device_admin"
                ? "Clear device admin"
                : `Lock → ${row.desired ?? "?"}`}
            </span>
            <Badge tone={commandStatusTone(row.status)}>{row.status}</Badge>
          </div>
          <div className="flex justify-between gap-3 text-slate-500">
            <span>stage {row.stage}</span>
            <span>{new Date(row.createdAt).toLocaleString()}</span>
          </div>
          {row.lastError ? (
            <p className="mt-1 break-words font-mono text-[10px] text-slate-500">{row.lastError}</p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function commandStatusTone(
  status: DeviceCommand["status"],
): "success" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "ack":
      return "success";
    case "sent":
    case "queued":
      return "warning";
    case "failed":
      return "danger";
  }
}

/** Render the UNKNOWN sentinel as "—" so the lock-events list doesn't read as
 *  broken — UNKNOWN is the normal value for a field with no signal yet. */
function dashUnknown(state: string): string {
  return state === "UNKNOWN" ? "—" : state;
}

/**
 * Best-known lock state for a lock event. android-do has no synchronous
 * provider, so `providerState` only advances when a lock command is
 * device-confirmed and is otherwise UNKNOWN; the DPC's snapshot (`deviceState`)
 * is authoritative. Prefer deviceState, fall back to providerState, then "—"
 * when neither is known yet (e.g. a freshly-issued, not-yet-confirmed command).
 */
function lockStateOf(row: LockEvent): string {
  if (row.deviceState !== "UNKNOWN") return row.deviceState;
  if (row.providerState !== "UNKNOWN") return row.providerState;
  return "—";
}

function LockEventsList({ loading, error, rows }: PanelProps<LockEvent>): JSX.Element {
  // Latest 5 by default (rows arrive newest-first); reveal the rest on
  // demand so a long-lived loan's lock history doesn't flood the page.
  const [expanded, setExpanded] = useState(false);
  if (loading || error || rows.length === 0) {
    return <PanelEmpty loading={loading} error={error} label="lock events" />;
  }
  const visible = expanded ? rows : rows.slice(0, 5);
  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {visible.map((row) => (
          <li
            key={row.id}
            className="flex flex-col gap-0.5 rounded-md border border-slate-200 px-3 py-2 text-xs"
          >
            <div className="flex justify-between gap-3">
              <span className="font-medium text-slate-900">{row.reason}</span>
              <span className="text-slate-500">{new Date(row.occurredAt).toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-3 text-slate-500">
              <span>
                state:{" "}
                <span className="font-mono font-medium text-slate-700">{lockStateOf(row)}</span>
              </span>
              <span>stage {row.stage}</span>
            </div>
            <div
              className="flex gap-3 text-[10px] text-slate-400"
              title="provider = synchronous adapter ACK; UNKNOWN for android-do, which confirms via device snapshots. device = the DPC's reported state (authoritative)."
            >
              <span>
                provider: <span className="font-mono">{dashUnknown(row.providerState)}</span>
              </span>
              <span>
                device: <span className="font-mono">{dashUnknown(row.deviceState)}</span>
              </span>
            </div>
            {row.notes ? (
              <p className="mt-1 truncate text-[10px] text-slate-500">{row.notes}</p>
            ) : null}
          </li>
        ))}
      </ul>
      {!expanded && rows.length > 5 ? (
        <button
          type="button"
          className="text-xs text-brand-600 hover:underline"
          onClick={() => setExpanded(true)}
        >
          Show {rows.length - 5} more
        </button>
      ) : null}
    </div>
  );
}

function CrossLink({
  label,
  to,
  id,
  subtitle,
}: {
  label: string;
  to: "/customers/$id" | "/loans/$id" | "/contracts/$id";
  id: string | undefined;
  subtitle: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
        {id ? (
          <Link
            to={to}
            params={{ id }}
            className="font-mono text-xs text-brand-600 hover:underline"
          >
            {id.slice(0, 8)}…
          </Link>
        ) : (
          <Badge tone="neutral">—</Badge>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-700">{subtitle}</p>
    </div>
  );
}
